import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { Plus, Users, Search, Phone, Mail, Calendar, Eye, Loader2, AlertTriangle } from 'lucide-react';
import { formatDate } from '@/utils/helpers';
import { formatFullName } from '@/utils/nameFormatter';

function MemberManagement() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortFilter, setSortFilter] = useState('latest');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [activeLoans, setActiveLoans] = useState([]); // Store active loans for status badges
  const [formData, setFormData] = useState({
    mandali_id: null,
    first_name: '',
    last_name: '',
    middle_name: '',
    nickname: '',
    contact_number: '',
    email: '',
    reference: '',
    opts_in_lavajam: true,
    opts_in_loan: true,
    opening_balance: 500,
    lavajam_amount: 500,
    joining_fee: 300,
  });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchMembers(searchQuery, sortFilter);
    }, searchQuery ? 500 : 0);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, sortFilter]);

  const handleContactChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, contact_number: value }));

    if (value.length >= 10) {
      try {
        const existing = await api.accountHolders.getAll({ search: value });
        if (existing && existing.length > 0) {
          // Exact match check
          const match = existing.find(m => m.contact_number === value);
          if (match) {
            setDuplicateWarning(match);
            setFormData(prev => ({
              ...prev,
              first_name: match.first_name,
              last_name: match.last_name,
              middle_name: match.middle_name || '',
              nickname: match.nickname || '',
              email: match.email || '',
              reference: match.reference || '',
            }));
            toast.warning(`Member found: ${formatFullName(match.first_name, match.middle_name, match.last_name)}. Details auto-filled.`);
          } else {
            setDuplicateWarning(null);
          }
        } else {
          setDuplicateWarning(null);
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      setDuplicateWarning(null);
    }
  };

  const fetchMembers = async (search = searchQuery, sort = sortFilter) => {
    try {
      // Fetch members and active loans in parallel for status badges
      const [data, loansData] = await Promise.all([
        api.accountHolders.getAll({ search, sort }),
        api.loans.getAll({ status: 'active' })
      ]);
      setMembers(data);
      setActiveLoans(loansData);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get member's loan payment status
  const getMemberPaymentStatus = (memberId) => {
    const loan = activeLoans.find(l => l.account_holder_id === memberId);
    if (!loan) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if loan end date has passed (overdue)
    if (loan.loan_end_date) {
      const endDate = new Date(loan.loan_end_date);
      endDate.setHours(0, 0, 0, 0);
      if (endDate < today && loan.outstanding_principal > 0) {
        return { status: 'overdue', label: 'OVERDUE', color: 'bg-red-100 text-red-700 border-red-200' };
      }
    }

    // Check if EMI is due this month (before 20th)
    const currentDay = today.getDate();
    if (currentDay < 20 && loan.outstanding_installments > 0) {
      return { status: 'due', label: 'DUE', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
    }

    // If past 20th and has outstanding, it's overdue for this month
    if (currentDay >= 20 && loan.outstanding_installments > 0) {
      return { status: 'due', label: 'DUE', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
    }

    // Loan is current/paid for this period
    return { status: 'current', label: 'CURRENT', color: 'bg-green-100 text-green-700 border-green-200' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Get user's mandali_id if not set
      if (!formData.mandali_id) {
        const mandalis = await api.mandali.getAll();
        if (mandalis.length > 0) {
          formData.mandali_id = mandalis[0].id;
        }
      }

      // Ensure boolean fields are explicitly boolean (not null or undefined)
      // Use === true to convert any truthy/falsy to explicit true/false
      // Convert empty strings to null for optional fields to avoid Pydantic validation errors
      const cleanedData = {
        ...formData,
        opts_in_lavajam: formData.opts_in_lavajam === true,
        opts_in_loan: formData.opts_in_loan === true,
        opening_balance: Number(formData.opening_balance) || 0,
        lavajam_amount: Number(formData.lavajam_amount) || 0,
        joining_fee: Number(formData.joining_fee) || 0,
        middle_name: formData.middle_name.trim() || null,
        nickname: formData.nickname.trim() || null,
        email: formData.email.trim() || null,
        reference: formData.reference.trim() || null
      };

      await api.accountHolders.create(cleanedData);
      toast.success('Account holder enrolled successfully!');
      setDialogOpen(false);
      fetchMembers();
      // Reset form
      setFormData({
        mandali_id: null,
        first_name: '',
        last_name: '',
        middle_name: '',
        nickname: '',
        contact_number: '',
        email: '',
        reference: '',
        opts_in_loan: false,
        opening_balance: 0,
        lavajam_amount: 500,
        joining_fee: 300,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll member');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in pt-2">
        {/* Header - Consistent spacing */}
        <div className="flex items-center justify-between flex-wrap gap-4 pb-2">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-1 gradient-text">
              Member Management
            </h1>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600" />
              Enroll and manage account holders
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg" data-testid="enroll-member-button">
                <Plus className="w-4 h-4 mr-2" />
                Enroll Member
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto p-4 md:p-6">
              <DialogHeader className="pb-4">
                <DialogTitle className="text-xl md:text-2xl gradient-text">Enroll New Account Holder</DialogTitle>
                <DialogDescription>
                  Add a new member to the mandali
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Personal Details */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    Personal Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        data-testid="first-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="middle_name">Middle Name</Label>
                      <Input
                        id="middle_name"
                        value={formData.middle_name}
                        onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                        data-testid="middle-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nickname">Nickname (for quick search)</Label>
                    <Input
                      id="nickname"
                      value={formData.nickname}
                      onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                      placeholder="Optional short identifier"
                      data-testid="nickname-input"
                    />
                  </div>
                </div>

                {/* Contact Details */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <Phone className="w-5 h-5 text-emerald-600" />
                    Contact Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_number">Contact Number *</Label>
                      <Input
                        id="contact_number"
                        value={formData.contact_number}
                        onChange={handleContactChange}
                        required
                        placeholder="10 digits"
                        pattern="[0-9]{10}"
                        data-testid="contact-input"
                      />
                      {duplicateWarning && (
                        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-medium">Member already exists!</p>
                            <p>Name: {formatFullName(duplicateWarning.first_name, duplicateWarning.middle_name, duplicateWarning.last_name)}</p>
                            <p>Account: {duplicateWarning.account_number}</p>
                            <p className="text-xs mt-1">Details have been auto-filled. Change the number to create a new profile.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email (Optional)</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="email@example.com"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reference">Reference</Label>
                    <Input
                      id="reference"
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      placeholder="Name/phone or another account number"
                      data-testid="reference-input"
                    />
                  </div>
                </div>

                {/* Account Options */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                  <h3 className="font-semibold text-gray-800">Account Options</h3>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="opts_in_lavajam"
                        checked={formData.opts_in_lavajam}
                        onCheckedChange={(checked) => setFormData({ ...formData, opts_in_lavajam: checked === true })}
                        data-testid="lavajam-checkbox"
                      />
                      <Label htmlFor="opts_in_lavajam" className="cursor-pointer">
                        Enable Lavajam Services
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="opts_in_loan"
                        checked={formData.opts_in_loan}
                        onCheckedChange={(checked) => setFormData({ ...formData, opts_in_loan: checked === true })}
                        data-testid="loan-checkbox"
                      />
                      <Label htmlFor="opts_in_loan" className="cursor-pointer">
                        Enable Loan Services
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="opening_balance">Opening Balance (₹)</Label>
                    <Input
                      id="opening_balance"
                      type="number"
                      step="0.01"
                      value={formData.opening_balance}
                      onChange={(e) => setFormData({ ...formData, opening_balance: parseFloat(e.target.value) || 0 })}
                      data-testid="opening-balance-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="lavajam_amount">Lavajam Amount (₹)</Label>
                      <Input
                        id="lavajam_amount"
                        type="number"
                        step="1"
                        value={formData.lavajam_amount}
                        onChange={(e) => setFormData({ ...formData, lavajam_amount: parseFloat(e.target.value) || 0 })}
                        data-testid="lavajam-amount-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="joining_fee">Joining Fee (₹)</Label>
                      <Input
                        id="joining_fee"
                        type="number"
                        step="1"
                        value={formData.joining_fee}
                        onChange={(e) => setFormData({ ...formData, joining_fee: parseFloat(e.target.value) || 0 })}
                        data-testid="joining-fee-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                    data-testid="submit-member-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enrolling...
                      </>
                    ) : (
                      'Enroll Member'
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1" disabled={submitting}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filter */}
        <Card className="glass border-0 shadow-soft">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  placeholder="Search by name, account number, or phone..."
                  className="pl-12 h-12"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={sortFilter} onValueChange={setSortFilter}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Latest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="a-z">Name (A-Z)</SelectItem>
                    <SelectItem value="z-a">Name (Z-A)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="skeleton h-56"></Card>
            ))}
          </div>
        ) : members.length === 0 ? (
          <Card className="glass border-0 shadow-soft">
            <CardContent className="pt-12 pb-12">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Users className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {searchQuery ? 'No members found' : 'No members enrolled yet'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery ? 'Try a different search term' : 'Enroll your first member to get started'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setDialogOpen(true)} className="bg-gradient-to-r from-emerald-600 to-teal-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Enroll Member
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member, index) => (
              <Card
                key={member.id}
                className="glass card-hover border-0 shadow-soft hover:shadow-medium"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`member-card-${member.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg text-white font-bold text-lg">
                        {member.first_name?.charAt(0) || ''}{member.last_name?.charAt(0) || ''}
                      </div>
                      <div>
                        <CardTitle className="text-base font-bold text-gray-800">
                          {formatFullName(member.first_name, member.middle_name, member.last_name)}
                        </CardTitle>
                        <p className="text-xs text-emerald-600 font-semibold mt-1">{member.account_number}</p>
                      </div>
                    </div>
                    {/* Payment Status Badge */}
                    {(() => {
                      const paymentStatus = getMemberPaymentStatus(member.id);
                      if (!paymentStatus) return null;
                      return (
                        <span className={`px-2 py-1 rounded-full text-xs font-bold border ${paymentStatus.color}`}>
                          {paymentStatus.label}
                        </span>
                      );
                    })()}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {member.nickname && (
                    <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      "{member.nickname}"
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{member.contact_number}</span>
                  </div>

                  {member.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="truncate" title={member.email}>{member.email}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>Joined: {formatDate(member.join_date)}</span>
                  </div>

                  <div className="flex gap-2 pt-2">
                    {member.opts_in_loan && (
                      <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                        Loan: Active
                      </span>
                    )}
                    {member.opts_in_lavajam && (
                      <span className="inline-block px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">
                        Lavajam: Active
                      </span>
                    )}
                  </div>

                  <div className="pt-3 border-t">
                    <Button
                      variant="outline"
                      className="w-full"
                      size="sm"
                      onClick={() => navigate(`/manager/members/${member.id}`)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default MemberManagement;
